import type { ComponentType, ReactNode } from "react";
import { Link } from "react-router";
import { useCanOpen } from "@/hooks/useCanOpen";
import { cn } from "@/lib/utils";

/**
 * EntityLink — the single visual primitive for linking to a record (child,
 * contact, employee, contract, invoice, debtor account). Every typed wrapper
 * renders through this so the object types look identical and are obviously
 * clickable everywhere: a leading type icon (persistent affordance) + the
 * primary colour + hover underline. Uses design tokens only (no hard-coded
 * colours).
 *
 * When the session cannot open the target (`useCanOpen`, e.g. an employee
 * without the `children` module), the label renders as plain text: a link
 * would only bounce them to another page.
 *
 * Clicking stops propagation so an entity link nested inside a clickable row
 * (e.g. an expandable invoice card or a ledger row) navigates to the object
 * instead of also triggering the row's handler.
 */
export interface EntityLinkProps {
  /** Router destination (path + optional query string). */
  to: string;
  /** Lucide icon component for the object type. */
  icon: ComponentType<{ className?: string; "aria-hidden"?: boolean }>;
  /** Visible label (already formatted to the canonical form by the wrapper). */
  children: ReactNode;
  /** Tooltip; defaults to the string label when one is passed. */
  title?: string;
  className?: string;
  "aria-label"?: string;
  /**
   * Optional test id forwarded to the rendered element. Lets a test-coupled
   * call site (e.g. the reconciliation review queue) swap a bespoke link for
   * the canonical component without breaking its `getByTestId` assertions.
   */
  "data-testid"?: string;
}

export function EntityLink({
  to,
  icon: Icon,
  children,
  title,
  className,
  ...rest
}: EntityLinkProps) {
  const canOpen = useCanOpen();
  if (!canOpen(to)) {
    return (
      <EntityLinkPlain
        icon={Icon}
        title={title}
        className={className}
        data-testid={rest["data-testid"]}
        muted={false}
      >
        {children}
      </EntityLinkPlain>
    );
  }
  return (
    <Link
      to={to}
      title={title}
      onClick={(e) => e.stopPropagation()}
      className={cn(
        "inline-flex items-center gap-1 font-medium text-primary underline-offset-2 hover:underline",
        className,
      )}
      {...rest}
    >
      <Icon className="h-3.5 w-3.5 shrink-0" aria-hidden />
      <span>{children}</span>
    </Link>
  );
}

export interface EntityLinkPlainProps
  extends Pick<EntityLinkProps, "icon" | "children" | "title" | "className" | "data-testid"> {
  /** Muted for a missing target (default); inherits the text colour for a record the session cannot open. */
  muted?: boolean;
}

/**
 * Plain (non-clickable) fallback: the wrappers use it when the target id is
 * missing, `EntityLink` when the session cannot open the target. Keeps the
 * canonical label + icon so the object still reads consistently, just without
 * navigation.
 */
export function EntityLinkPlain({
  icon: Icon,
  children,
  title,
  className,
  "data-testid": dataTestId,
  muted = true,
}: EntityLinkPlainProps) {
  return (
    <span
      title={title}
      data-testid={dataTestId}
      className={cn(
        "inline-flex items-center gap-1 font-medium",
        muted && "text-muted-foreground",
        className,
      )}
    >
      <Icon className="h-3.5 w-3.5 shrink-0" aria-hidden />
      <span>{children}</span>
    </span>
  );
}
